// @flow
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import getHybridTier from 'app/common/react/components/growth/unauth/open/hybrid/hybridExperimentUtils';
import ProfilePage from 'app/common/react/components/profile/ProfilePage/ProfilePage';
import type { Router } from 'app/common/lib/ReactRouter/types';
import type { State as ReduxState } from 'app/www-unauth/redux/reducers/types';
import UNAUTH_PAGE_TYPE from 'app/common/react/components/growth/unauth/lib/UnauthPageType';
import UnauthErrorPage from 'app/common/react/components/growth/unauth/UnauthErrorPage/UnauthErrorPage';
import UnauthPageWrapper from 'app/www-unauth/components/UnauthPageWrapper/UnauthPageWrapper';
import UnauthPageWrapperContext from 'app/www-unauth/components/UnauthPageWrapper/UnauthPageWrapperContext';
import withResource from 'app/common/lib/react/withResource';
import withRouteData from 'app/common/lib/react/withRouteData';
import { compose } from 'redux';
import { locationToErrorMapEntryUnset } from 'app/common/redux/actions/mainComponent';
import { logCustomTraceEvent } from 'app/common/lib/TraceLogger/logTraceEvent';
import { ProfileSnippet } from 'app/common/react/components/growth/unauth/pin/RichSnippet/ProfileSnippet';
import { useRequestContext } from 'app/common/react/components/RequestContext';
import { useUnauthContext } from 'app/common/react/components/growth/unauth/UnauthContext';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';

type ReduxStateProps = {|
  locationToErrorMap: Object,
  isShowEUCookieBar: boolean,
|};

type OwnProps = {|
  params: { username: string },
  routes: [any, any, { path?: 'boards' | 'pins' | 'tried' }],
  route: {
    legacy_server_context: {
      is_mobile_agent: boolean,
      user: {
        username: string,
      },
    },
  },
  children: any,
  dispatch: Function,
  location: { pathname: string },
  userNode: {
    isLoaded: boolean,
    data: Object,
  },
  router: Router,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
|};

export function ProfilePageContainer(props: Props): any {
  const requestContext = useRequestContext();
  const { country, isBot } = requestContext;
  const unauthContext = useUnauthContext();

  useEffect(() => {
    logCustomTraceEvent({ name: 'UnauthProfileInteractiveTraceEvent' });
  }, []);

  function hybridTier() {
    const hybridTier = getHybridTier(
      country,
      isBot,
      unauthContext && unauthContext.httpReferrer,
      props.seoUnauthExperiments,
    );
    return hybridTier;
  }

  function unsetError() {
    props.dispatch(locationToErrorMapEntryUnset(props.location.pathname));
  }

  const tab = props.routes.length > 1 ? props.routes[2].path : 'boards';
  const user = props.userNode;
  if (user.data) {
    const profile = user.data.user;
    return (
      <UnauthPageWrapper hybridTier={hybridTier()} pageType={UNAUTH_PAGE_TYPE.PROFILE}>
        <UnauthPageWrapperContext.Consumer>
          {({ showSignupModal }) => (
            <React.Fragment>
              <ProfilePage
                username={props.params.username}
                showPulsar={false}
                tab={tab}
                renderError={props.locationToErrorMap[props.location.pathname]}
                unsetError={unsetError}
                router={props.router}
                user={{ data: profile, isLoaded: true }}
                showSignupModal={showSignupModal}
                isShowEUCookieBar={props.isShowEUCookieBar}
              >
                {React.cloneElement(props.children, { requestContext, user })}
              </ProfilePage>
              <ProfileSnippet user={profile} />
            </React.Fragment>
          )}
        </UnauthPageWrapperContext.Consumer>
      </UnauthPageWrapper>
    );
  } else {
    return user.error ? <UnauthErrorPage /> : null;
  }
}

export default compose(
  withRouteData({
    name: 'ProfilePage',
    upwtActionName: PCONST.thrift.TracingClientTypes.PWTAction.USER_PAGE_INTERACTIVE,
  }),
  withResource({
    name: 'UserProfileBaseResource',
    key: 'userNode',
    options: ({ params }) => ({
      field_set_key: 'react_profile',
      username: params.username,
      get_page_metadata: true,
      force_node: true,
    }),
  }),
  connect<Props, OwnProps, ReduxStateProps, _, _, _>(
    (state: ReduxState): ReduxStateProps => ({
      locationToErrorMap: state.ui.mainComponent.locationToErrorMap,
      isShowEUCookieBar: state.euCookieBar.isShowEUCookieBar,
    }),
  ),
  withUnauthSeoExperiments,
)(ProfilePageContainer);
